// CONTRACT:I2-SECURITY-LABELS.1.0 / CONTRACT:P1-CANONICAL-ENVELOPE.1.0
/**
 * marking-shape — the BUNDLE-FREE shape/parse primitive of the three-case marking
 * taxonomy (docs/AOA-CLASSID-RECOMPUTE.md "Fail-closed reconciliation — the
 * three-case marking taxonomy").
 *
 * THE SINGLE SOURCE OF TRUTH for the shape half of the taxonomy. Both the COP's
 * local PDP (apps/cop/src/host/pdp.ts → classifyMarking, which adds the bundle-
 * driven validateMarkings.valid contradiction check on top) AND the bundle-free
 * tak-gateway (services/tak-gateway/src/cot.ts, which has NO policy bundle) import
 * these so their broken/unmarked detection can never drift (avoids FINDINGS
 * F-LATTICE-1: two re-implementations of the same lattice silently diverging).
 *
 * The taxonomy distinguishes THREE cases purely from the security view's shape:
 *   Case 1 UNMARKED        — no marking present at all (no classification AND no
 *                            binding list fields). A CONFIGURABLE default class,
 *                            NOT a drop. `isUnmarked` is true.
 *   Case 2 CLEAN           — marking present, well-shaped, canonicalizes. (Bundle-
 *                            driven contradiction-validity, e.g. NOFORN ⊥ REL, is
 *                            layered on TOP of this in the PDP; the gateway, having
 *                            no bundle, can only assert SHAPE — contradiction
 *                            checking stays the PDP's job downstream.)
 *   Case 3 BROKEN-PRESENT  — a marking is PRESENT but its SHAPE is malformed: a
 *                            list field that is not a string[], a non-string scalar
 *                            where a string is required, or a classification that is
 *                            present-but-empty/whitespace. `isWellShaped` is false.
 *
 * Pure: zero DOM / network / crypto / policy bundle. Operates on an untrusted
 * `unknown` security view (the gateway parses raw XML attrs; the PDP holds typed
 * envelopes) so both call sites can hand it pre-validation data.
 */

/**
 * Shape-validate a security view BEFORE any bundle-driven derivation. A malformed
 * shape (e.g. `releasability` arriving as a bare string instead of string[]) would
 * make downstream functions iterate a string as characters — a silent merge-leak.
 *
 * Returns false ⇒ Case 3 (BROKEN-PRESENT) when ANY of:
 *   - the view is null / not an object;
 *   - `classification` is present but not a string;
 *   - any list field (releasability / caveats / mission) is present but not an
 *     array, or contains a non-string element;
 *   - an isProtected input (payload_mode / leakage_profile) is present but not a
 *     string (=== / !== comparisons depend on it being a string).
 *
 * `strictEmptyClassification` (default false — the PDP's typed-envelope contract,
 * where an empty classification is treated as ABSENT → Case 1 unmarked, the existing
 * byte-identical behavior the lockstep oracle depends on). The GATEWAY parses raw
 * XML attrs, where a `@_classification` attr that is PRESENT-but-empty/whitespace is
 * a broken marking node that must NOT collapse to the unmarked default and leak — it
 * passes `true` so a present-but-blank classification is rejected as Case 3.
 */
export function isWellShaped(security: unknown, strictEmptyClassification = false): boolean {
  if (security == null || typeof security !== "object") return false;
  // A bare array passes `typeof === "object"` and binds none of the marking facets,
  // so without this guard it would be judged UNMARKED (Case 1 → permit) rather than
  // BROKEN. Defense-in-depth: a security view is an object LITERAL, never an array, so
  // an array here is a malformed/never-typed input — treat it as Case 3 (broken/deny)
  // rather than letting it inherit the legacy-permissive unmarked default.
  if (Array.isArray(security)) return false;
  const s = security as Record<string, unknown>;
  // classification: optional, but if present must be a string.
  if (s.classification != null) {
    if (typeof s.classification !== "string") return false;
    // Gateway-strict: a present-but-blank classification is a broken marking node,
    // NEVER coerced to empty → unmarked (the dangerous Case 3 → Case 1 leak).
    if (strictEmptyClassification && s.classification.trim() === "") return false;
  }
  // list fields: if present must be arrays of strings.
  for (const k of ["releasability", "caveats", "mission"] as const) {
    const v = s[k];
    if (v == null) continue;
    if (!Array.isArray(v)) return false;
    for (const el of v) if (typeof el !== "string") return false;
  }
  // isProtected inputs: if present must be strings (=== / !== comparisons).
  if (s.payload_mode != null && typeof s.payload_mode !== "string") return false;
  if (s.leakage_profile != null && typeof s.leakage_profile !== "string") return false;
  return true;
}

/**
 * Is a (well-shaped) security view UNMARKED (Case 1)? No classification AND no
 * non-empty binding list fields. An unmarked view is treated as the CONFIGURED
 * default class, not dropped. Caller MUST gate this behind isWellShaped — a broken
 * shape is Case 3, never Case 1.
 */
export function isUnmarked(security: unknown): boolean {
  const s = security as Record<string, unknown>;
  const cls = s.classification;
  // NOTE `!== ""` (NOT `.trim()`) — byte-identical to the PDP's original isUnmarked
  // so the lockstep oracle stays unchanged. A present-but-whitespace classification
  // is caught upstream by isWellShaped(strict) in the gateway path, not re-judged here.
  const hasCls = typeof cls === "string" && cls !== "";
  const hasList = (k: string): boolean => Array.isArray(s[k]) && (s[k] as unknown[]).length > 0;
  return !hasCls && !hasList("releasability") && !hasList("caveats") && !hasList("mission");
}

/** The shape-only marking case (the bundle-free subset of the three-case taxonomy). */
export type MarkingShapeCase = "unmarked" | "broken" | "well-shaped";

/**
 * Classify a security view by SHAPE alone (no bundle). "well-shaped" means present
 * and structurally valid — the PDP still layers contradiction-validity on top to
 * decide CLEAN vs BROKEN; the gateway treats "well-shaped" as good-to-publish and
 * leaves contradiction checking to the downstream PDP. This is the canonical
 * detector both call sites share, and the drift-guard asserts equality against.
 */
export function classifyMarkingShape(
  security: unknown,
  strictEmptyClassification = false,
): MarkingShapeCase {
  if (!isWellShaped(security, strictEmptyClassification)) return "broken";
  if (isUnmarked(security)) return "unmarked";
  return "well-shaped";
}
